import React, { createContext, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { money } from './ecourtUtils';

/* ---------- Balance (credits + wallet) shared by every eCourts page ---------- */

export const BalanceContext = createContext({ balance: null, setBalance: () => {} });

/** { balance, prices, note(resp) } — note() picks up `balance` from any paid response. */
export function useBalance() {
  const { balance, setBalance } = useContext(BalanceContext);
  const note = useCallback((resp) => {
    if (resp && resp.balance) setBalance(resp.balance);
    return resp;
  }, [setBalance]);
  return { balance, prices: balance && balance.prices, note, setBalance };
}

/** "12.40 credits · ₹50.00 wallet" pill (1 credit = ₹1). */
export function BalancePill() {
  const { balance } = useBalance();
  if (!balance) return null;
  const credits = Number(balance.credits) || 0;
  const wallet = Number(balance.wallet) || 0;
  const low = credits < 1 && wallet < 1;
  return (
    <span className={`ec-badge ${low ? 'ec-badge-orange' : 'ec-badge-grey'} align-self-center`}
      title="1 credit = ₹1. eCourts lookups use credits first, then your wallet. Saved results are free.">
      {credits.toFixed(2)} credits · {money(wallet)} wallet
    </span>
  );
}

/** Price tag inside a button, e.g. "1.50" or "0.15 × 4". Hidden until prices load. */
export function Cost({ kind, qty, free }) {
  const { prices } = useBalance();
  if (free) return <span className="ec-cost">free</span>;
  if (!prices || prices[kind] == null) return null;
  const unit = Number(prices[kind]);
  const perItem = (prices.perItem || []).includes(kind);
  const n = perItem && qty > 1 ? qty : 1;
  return <span className="ec-cost">{perItem && n > 1 ? `${unit.toFixed(2)} × ${n}` : unit.toFixed(2)}</span>;
}

/** "Saved · free" or "₹1.50 from credits" line after a paid action. */
export function ChargeNote({ resp }) {
  if (!resp) return null;
  if (resp.cached) return <span className="ec-badge ec-badge-grey">Saved · free</span>;
  if (resp.charged && resp.charged.amount) {
    return <span className="small ec-muted">{money(resp.charged.amount)}{resp.charged.from ? ` from ${resp.charged.from}` : ''}</span>;
  }
  return null;
}

/* ---------- common bits ---------- */

export function Loader({ label = 'Loading…' }) {
  return (
    <div className="d-flex align-items-center gap-2 ec-muted py-3" role="status">
      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

const FRIENDLY = {
  NETWORK: 'Cannot reach the server. Check your connection.',
  TIMEOUT: 'This is taking too long. Please try again.',
  RATE_LIMITED: 'Too many requests. Please wait a minute and try again.',
  ECOURTS_NOT_CONFIGURED: 'eCourts is not set up on the server yet.',
  UPSTREAM_TIMEOUT: 'The court data service is slow right now. Please try again.',
  UPSTREAM_UNREACHABLE: 'The court data service is not reachable right now. Please try again.',
};

export function ErrorBox({ error, onRetry }) {
  if (!error) return null;
  const msg = FRIENDLY[error.code] || error.message || 'Something went wrong';
  return (
    <div className="ec-alert ec-alert-error d-flex flex-wrap align-items-center gap-2 my-3" role="alert">
      <span className="flex-grow-1">{msg}</span>
      {error.code === 'INSUFFICIENT_BALANCE' && <Link to="/Subscription" className="btn btn-sm btn-ec">Recharge</Link>}
      {onRetry && error.code !== 'INSUFFICIENT_BALANCE' && <button type="button" className="btn btn-sm btn-ec-outline" onClick={onRetry}>Try again</button>}
    </div>
  );
}

export function Empty({ children }) {
  return <div className="ec-empty">{children}</div>;
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
      <div>
        <h1 className="ec-h1 mb-1">{title}</h1>
        {subtitle && <p className="ec-muted mb-0">{subtitle}</p>}
      </div>
      <div className="d-flex flex-wrap gap-2 align-items-center">
        {children}
        <BalancePill />
      </div>
    </div>
  );
}

export function BackLink({ to, children }) {
  return (
    <Link to={to} className="ec-back">
      <FontAwesomeIcon icon={faChevronLeft} /> {children}
    </Link>
  );
}

export function Segmented({ value, options, onChange, label }) {
  return (
    <div className="ec-segmented" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value}
          className={value === o.value ? 'active' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}